import { Amplify } from 'aws-amplify';
import {
  ErrorNotReceivedMessageWrongLogin,
  UserShouldRedefineHisPword,
  UserShouldVerifySMS,
} from '../../shared/messages/errors';
import {
  RecoveryCodeWasSent,
  ShouldVerifyEmail,
  ShouldVerifyEmailFMA,
  ShouldVerifySMS,
} from '../../shared/messages/messages';
import LoginErrorHandler from './handleLoginErrors';
import { UserHelper } from './UserHelper';
import { JWTHelper, refreshToken } from './JWTHelper';
import { doRequest, doPost, doGet } from '../http/httpHelper';
import Cookies from 'js-cookie';
import {
  confirmSignIn,
  confirmSignUp,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  signOut as amplifySignOut,
  updateUserAttributes,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';

const resolvePatientV2Profile = (value, isAdmin) => value || (isAdmin === '1' || isAdmin === true ? 'avaliador_regulatorio' : 'solicitante');

export const LoginService = new (class LoginService {
  user = null;
  constructor() {
    Amplify.configure(
      {
        Auth: {
          Cognito: {
            userPoolId: process.env.REACT_APP_USER_POOL_ID,
            userPoolClientId: process.env.REACT_APP_USER_POOL_WEBCLIENT_ID,
            loginWith: {
              email: true,
            },
          },
        },

        API: {
          REST: {
            portalApi: {
              endpoint: process.env.REACT_APP_API_URL_DOMAIN,
              region: process.env.REACT_APP_AMPLIFY_REGION,
            },
          },
        },
      },
      {
        API: {
          REST: {
            headers: async () => {
              const session = await fetchAuthSession().catch(() => null);
              const idToken = session?.tokens?.idToken?.toString?.();
              const alternativeIdToken =
                localStorage.getItem('idToken') ||
                sessionStorage.getItem('idTokenMFA');
              return {
                Authorization: idToken
                  ? `Bearer ${idToken}`
                  : `Bearer ${alternativeIdToken || ''}`,
              };
            },
          },
        },
      }
    );
  }

  async getLoggedUserPayload() {
    let idToken, refreshToken, user, attrs;
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      idToken = session?.tokens?.idToken?.toString?.() || '';
      refreshToken = session?.tokens?.refreshToken?.toString?.() || '';
      user = await getCurrentUser();
      attrs = await fetchUserAttributes();
    } catch (error) {
      console.log('ocorreu um erro buscando o usuario: ', error);
      idToken = localStorage.getItem('idToken');
      refreshToken = '';
      user = JSON.parse(atob(idToken.split('.')[1]));
      attrs = user;
    }

    return {
      username: user.username || attrs['cognito:username'],
      name: attrs.name,
      cnpj: attrs['custom:cnpj'],
      isSpecialist: attrs['custom:isSpecialist'],
      shouldSignPrivacyTerm: attrs['custom:privacyTermSigned'] !== '1',
      isGeneralist: attrs['custom:isGeneralist'],
      isManager: attrs['custom:isManager'],
      isWhiteMartins: attrs['custom:isWhiteMartins'],
      canSeeAllCnpjs: attrs['custom:canSeeAllCnpjs'],
      isAdmin: attrs['custom:isAdmin'],
      patientV2Profile: resolvePatientV2Profile(attrs['custom:patientV2Profile'], attrs['custom:isAdmin']),
      token: idToken,
      refreshToken: '',
      companyName: attrs['custom:companyName'],
      email: attrs.email,
      accessImplantacao: attrs['custom:accessImplantacao'],
      accessAssistencia: attrs['custom:accessAssistencia'],
      accessRecarga: attrs['custom:accessRecarga'],
      accessRecolhimento: attrs['custom:accessRecolhimento'],
      accessVenda: attrs['custom:accessVenda'],
      accessSubalocacao: attrs['custom:accessSubalocacao'],
      accessPremium: attrs['custom:accessPremium'],
      accessReport: attrs['custom:accessReport'],
      accessPremium: attrs['custom:accessPremium'],
      canEditPaymentList: attrs['custom:canEditPaymentList'],
      email_verified: attrs.email_verified,
    };
  }

  async enviarCodigoVerificacaoTelefone() {
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });
      return { success: true };
    } catch (error) {
      console.error('Erro ao enviar código de verificação:', error);
      return { success: false, error };
    }
  }

  async confirmarCodigoTelefone(codigo) {
    try {
      await confirmUserAttribute({
        userAttributeKey: 'phone_number',
        confirmationCode: codigo,
      });
      return { success: true };
    } catch (error) {
      console.error('Erro ao verificar telefone:', error);
      return { success: false, error };
    }
  }

  async atualizarTelefone(novoTelefone) {
    try {
      await updateUserAttributes({
        userAttributes: { phone_number: novoTelefone },
      });
      return { success: true };
    } catch (error) {
      console.error('Erro ao atualizar telefone:', error);
      return { success: false, error };
    }
  }

  async doLogin(email, password, captchaValue) {
    try {
      // Se este login veio de uma tentativa de SSO sem match, carrega o vínculo pendente
      // (id opaco gravado pela tela de login). O backend usa isso para criar o vínculo.
      const ssoPending = sessionStorage.getItem('ssoPending') || undefined;
      const result = await doPost('/login', {
        email,
        password,
        captchaValue,
        ...(ssoPending ? { ssoPending } : {}),
      });
      console.log('result', result);
      if (result.isSignedIn) {
        localStorage.setItem('idToken', result.Session);
        sessionStorage.removeItem('ssoPending');
        console.log('logado com suceso');
      }
      if (!result.isSignedIn) {
        const step = result.nextStep?.signInStep;
        if (step === 'NEW_PASSWORD_REQUIRED') {
          sessionStorage.setItem('idTokenSetPassword', result.Session);
          return {
            status: { code: 400, message: UserShouldRedefineHisPword },
          };
        }

        if (step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' || step === 'EMAIL_OTP' || step === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          localStorage.removeItem('idToken');
          sessionStorage.setItem('idTokenMFA', result.Session);
          return {
            status: { code: 202, message: ShouldVerifyEmailFMA },
            challenge: 'EMAIL_MFA',
          };
        }

        if (step === 'RESET_PASSWORD') {
          return {
            status: {
              code: 401,
              message: 'Houve um problema durante a autenticação, verifique se o email ou a senha digitada está correta',
            },
          };
        }

        return {
          status: {
            code: 400,
            message: 'Fluxo de autenticação não suportado.',
          },
          debug: { step },
        };
      }

      const fullUser = await this.getLoggedUserPayload();

      if (!fullUser?.token) {
        return {
          status: {
            code: 400,
            message: 'Sessão inválida. Faça login novamente.',
          },
        };
      }

      return {
        status: { code: 200 },
        user: fullUser,
      };
    } catch (error) {
      const name = error?.name || error?.code;
      console.log('Erro durante o login: ', name);

      // Tenta extrair o reason do corpo da resposta (ex: MISSING_CAPTCHA)
      const reason =
        error?.response?.data?.body?.message?.nextStep?.additionalInfo
          ?.reason ||
        error?.body?.message?.nextStep?.additionalInfo?.reason ||
        '';

      return {
        status: {
          code: 400,
          message: new LoginErrorHandler().handle(error),
          reason,
        },
        debug: {
          name: error?.name,
          code: error?.code,
          message: error?.message,
        },
      };
    }
  }

  // ─── ALTERADO: agora retorna `reason` junto ao status para mapeamento de mensagens ───
  async confirmEmailOtp(code, email) {
    try {
      const session = sessionStorage.getItem('idTokenMFA');
      // Propaga o vínculo pendente também na etapa do OTP (é aqui que o login normalmente conclui).
      const ssoPending = sessionStorage.getItem('ssoPending') || undefined;
      console.log('confirmEmailOtp', { email, session, code });
      const result = await doPost('/verify', {
        email,
        session,
        code,
        ...(ssoPending ? { ssoPending } : {}),
      });

      if (result.isSignedIn) {
        localStorage.setItem('idToken', result.Session);
        sessionStorage.removeItem('idTokenMFA');
        sessionStorage.removeItem('ssoPending');
        console.log('logado com suceso');
      }

      if (!result?.isSignedIn) {
        // Extrai o motivo da falha para permitir mapeamento de mensagem amigável no caller
        const reason = result?.nextStep?.additionalInfo?.reason || '';
        return {
          status: {
            code: 400,
            message: reason || 'Não foi possível concluir o login.',
            reason, // ← campo adicionado
          },
          debug: { nextStep: result?.nextStep },
        };
      }

      const fullUser = await this.getLoggedUserPayload();

      if (!fullUser?.token) {
        return {
          status: {
            code: 400,
            message: 'Sessão inválida após verificação. Faça login novamente.',
            reason: '',
          },
        };
      }

      return {
        status: { code: 200 },
        user: fullUser,
      };
    } catch (error) {
      return {
        status: {
          code: 400,
          message: new LoginErrorHandler().handle(error),
          reason: '',
        },
      };
    }
  }

  async confirmSignup(username, code) {
    try {
      await confirmSignUp({ username, confirmationCode: code });
      return true;
    } catch (error) {
      console.error('Erro ao confirmar conta:', error);
      return false;
    }
  }

  async sendCode(codigoDigitado) {
    try {
      const username = Cookies.get('ssoUsername');
      const authSource = Cookies.get('authSource');
      const ssoChallengeSession = Cookies.get('ssoChallengeSession');

      const userPool = new CognitoUserPool({
        UserPoolId: process.env.REACT_APP_USER_POOL_ID,
        ClientId: process.env.REACT_APP_USER_POOL_WEBCLIENT_ID,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.Session = ssoChallengeSession;
      const challengeHandler = authSource === 'SSO' ? cognitoUser.sendCustomChallengeAnswer.bind(cognitoUser) : cognitoUser.sendMFACode.bind(cognitoUser);

      return new Promise((resolve, reject) => {
        challengeHandler(codigoDigitado, {
          onSuccess: (session) => {
            const tokens = {
              idToken: session.getIdToken().getJwtToken(),
              accessToken: session.getAccessToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken(),
            };
            resolve({ status: { code: 200 }, tokens });
          },
          onFailure: (err) => {
            console.error('Erro ao responder desafio MFA:', err);
            reject({
              status: {
                code: 400,
                message: err.message || 'Erro ao responder desafio MFA',
              },
            });
          },
        });
      });
    } catch (error) {
      console.error('Erro inesperado no desafio MFA:', error);
      return {
        status: { code: 500, message: error.message || 'Erro inesperado' },
      };
    }
  }

  async createSSOSession(userInfo, idTokenFromCognito) {
    try {
      const idCognitoToken = new CognitoIdToken({
        IdToken: idTokenFromCognito,
      });

      const dummyAccessToken = new CognitoAccessToken({
        AccessToken: idTokenFromCognito,
      });
      const dummyRefreshToken = new CognitoRefreshToken({
        RefreshToken: idTokenFromCognito,
      });

      const cognitoUserSession = new CognitoUserSession({
        AccessToken: dummyAccessToken,
        IdToken: idCognitoToken,
        RefreshToken: dummyRefreshToken,
      });

      const cognitoUserPool = new CognitoUserPool({
        UserPoolId: process.env.REACT_APP_USER_POOL_ID,
        ClientId: process.env.REACT_APP_USER_POOL_WEBCLIENT_ID,
      });

      const cognitoUser = new CognitoUser({
        Username: userInfo.username || userInfo['cognito:username'],
        Pool: cognitoUserPool,
      });
      console.log('cognitoUserSession', cognitoUserSession);
      cognitoUser.setSignInUserSession(cognitoUserSession);

      const userAttrs = JWTHelper.decode(idTokenFromCognito);
      console.log('userAttrs', userAttrs);
      const fullUser = {
        username: userAttrs['cognito:username'],
        name: userAttrs['name'],
        cnpj: userAttrs['custom:cnpj'],
        companyName: userAttrs['custom:companyName'],
        email: userAttrs['email'],
        email_verified: userAttrs['email_verified'],
        isSpecialist: userAttrs['custom:isSpecialist'],
        isGeneralist: userAttrs['custom:isGeneralist'],
        isManager: userAttrs['custom:isManager'],
        isWhiteMartins: userAttrs['custom:isWhiteMartins'],
        canSeeAllCnpjs: userAttrs['custom:canSeeAllCnpjs'],
        isAdmin: userAttrs['custom:isAdmin'],
        patientV2Profile: resolvePatientV2Profile(userAttrs['custom:patientV2Profile'], userAttrs['custom:isAdmin']),
        shouldSignPrivacyTerm: userAttrs['custom:privacyTermSigned'] !== '1',
        accessImplantacao: userAttrs['custom:accessImplantacao'],
        accessAssistencia: userAttrs['custom:accessAssistencia'],
        accessRecarga: userAttrs['custom:accessRecarga'],
        accessRecolhimento: userAttrs['custom:accessRecolhimento'],
        accessVenda: userAttrs['custom:accessVenda'],
        accessSubalocacao: userAttrs['custom:accessSubalocacao'],
        accessPremium: userAttrs['custom:accessPremium'],
        accessReport: userAttrs['custom:accessReport'],
        accessPremium: userAttrs['custom:accessPremium'],
        canEditPaymentList: userAttrs['custom:canEditPaymentList'],
        token: idTokenFromCognito,
        refreshToken: '',
      };

      sessionStorage.setItem('portalHomeCareAuth', JSON.stringify(fullUser));

      return {
        status: { code: 200 },
        user: fullUser,
      };
    } catch (error) {
      console.error('[createSSOSession] Erro ao criar sessão SSO: ', error);
      return {
        status: { code: 400, message: error.message || 'Erro desconhecido' },
      };
    }
  }

  async doAuth(email, password, keepConnected, captchaValue) {
    const resp = await this.doLogin(email, password, captchaValue);
    if (resp?.status?.code === 200) {
      let storage = sessionStorage;
      if (keepConnected) {
        storage = localStorage;
      }
      localStorage.setItem('keepConnected', keepConnected.toString());

      storage.setItem('portalHomeCareAuth', JSON.stringify(resp.user));

      return {
        logged: true,
        user: resp.user,
      };
    }
    return {
      logged: false,
      message: resp?.status?.message || ErrorNotReceivedMessageWrongLogin,
    };
  }

  async redefinePassword(email, newPassword, keepConnected) {
    try {
      const session = sessionStorage.getItem('idTokenSetPassword');
      console.log('redefinePassword', { email, newPassword });
      const result = await doPost('/new-password', {
        email,
        newPassword,
        session,
      });
      if (result.isSignedIn) {
        localStorage.setItem('idToken', result.Session);
        sessionStorage.removeItem('idTokenSetPassword');
        console.log('logado com suceso');
      }

      const fullUser = await this.getLoggedUserPayload();

      if (
        fullUser?.email_verified !== undefined &&
        fullUser.email_verified.toString() === 'false'
      ) {
        await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
        return {
          logged: false,
          message: ShouldVerifyEmail,
        };
      }

      let storage = sessionStorage;
      if (keepConnected) {
        storage = localStorage;
      }
      storage.setItem('portalHomeCareAuth', JSON.stringify(fullUser));

      return { logged: true, user: fullUser };
    } catch (error) {
      return {
        logged: false,
        message: new LoginErrorHandler().handle(error),
      };
    }
  }

  async verifyEmail(verificationCode, keepConnected) {
    try {
      await confirmUserAttribute({
        userAttributeKey: 'email',
        confirmationCode: verificationCode,
      });

      const fullUser = await this.getLoggedUserPayload();

      let storage = sessionStorage;
      if (keepConnected) {
        storage = localStorage;
      }
      storage.setItem('portalHomeCareAuth', JSON.stringify(fullUser));

      return { logged: true, user: fullUser };
    } catch (error) {
      return {
        logged: false,
        message: new LoginErrorHandler().handle(error),
      };
    }
  }

  async verifyAccessToken(code) {
    try {
      console.warn('Verifying access token code:', code);
      throw new Error('AuthError: Verify token invalid');
    } catch (error) {
      return {
        logged: false,
        message: new LoginErrorHandler().handle(error),
      };
    }
  }

  async checkSession(currentUser) {
    try {
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString?.();

      let storage = localStorage;
      currentUser.token = idToken || currentUser.token;

      storage.setItem('portalHomeCareAuth', JSON.stringify(currentUser));
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  isLogged() {
    const userFromIdtoken = this.getUserFromStorageIdToken();
    const currentUser = UserHelper.getCurrentUser() || userFromIdtoken;
    const isTokenExpired = JWTHelper.isExpired(currentUser?.token);
    const keepConnected = localStorage.getItem('keepConnected') === 'true';

    if (!isTokenExpired) {
      return true;
    }

    if (keepConnected) {
      return this.checkSession(currentUser);
    }

    return false;
  }

  getUserFromStorageIdToken() {
    try {
      const idToken = sessionStorage.getItem('idToken');
      const user = JSON.parse(atob(idToken.split('.')[1]));
      return {
        cnpj: user['custom:cnpj'],
        companyName: user['custom:companyName'],
        email: user['email'],
        isAdmin: user['custom:isAdmin'],
        patientV2Profile: resolvePatientV2Profile(user['custom:patientV2Profile'], user['custom:isAdmin']),
        isGeneralist: user['custom:isGeneralist'],
        isManager: user['custom:isManager'],
        isSpecialist: user['custom:isSpecialist'],
        isWhiteMartins: user['custom:isWhiteMartins'],
        canSeeAllCnpjs: user['custom:canSeeAllCnpjs'],
        accessImplantacao: user['custom:accessImplantacao'],
        accessAssistencia: user['custom:accessAssistencia'],
        accessRecarga: user['custom:accessRecarga'],
        accessRecolhimento: user['custom:accessRecolhimento'],
        accessVenda: user['custom:accessVenda'],
        accessSubalocacao: user['custom:accessSubalocacao'],
        accessPremium: user['custom:accessPremium'],
        accessReport: user['custom:accessReport'],
        accessPremium: user['custom:accessPremium'],
        canEditPaymentList: user['custom:canEditPaymentList'],
        name: user['name'],
        shouldSignPrivacyTerm: user['custom:privacyTermSigned'] !== '1',
        token: idToken,
        refreshToken: '',
        username: user['cognito:username'],
      };
    } catch (err) {
      console.log('Ocorreu umk erro em getUserFromStorageIdToken: ', err);
      return undefined;
    }
  }

  currentUser() {
    const auth = JSON.parse(
      sessionStorage.getItem('portalHomeCareAuth') || '{}'
    );

    if (auth.name) {
      return auth;
    }
    const authLocalStorage = JSON.parse(
      localStorage.getItem('portalHomeCareAuth') || '{}'
    );
    if (authLocalStorage.name) {
      return authLocalStorage;
    }
    return {
      logged: false,
      name: '',
      cnpj: '',
      email: '',
      companyName: '',
    };
  }

  setPrivacyTermAccepted() {
    const auth = JSON.parse(
      sessionStorage.getItem('portalHomeCareAuth') || '{}'
    );

    if (auth.name) {
      auth.shouldSignPrivacyTerm = false;
      sessionStorage.setItem('portalHomeCareAuth', JSON.stringify(auth));
      return auth;
    }
    const authLocalStorage = JSON.parse(
      localStorage.getItem('portalHomeCareAuth') || '{}'
    );
    if (authLocalStorage.name) {
      authLocalStorage.shouldSignPrivacyTerm = false;
      localStorage.setItem(
        'portalHomeCareAuth',
        JSON.stringify(authLocalStorage)
      );
      return authLocalStorage;
    }
    return {
      logged: false,
      name: '',
      cnpj: '',
      email: '',
      companyName: '',
    };
  }

  async forgotPassword(email, captchaValue) {
    try {
      const result = await doPost('/forgot-password', { email, captchaValue });
      return {
        logged: false,
        sentLink: true,
        message: RecoveryCodeWasSent(email),
      };
    } catch (error) {
      return {
        logged: false,
        message: new LoginErrorHandler().handle(error),
      };
    }
  }

  async recoveryPassword(email, newPassword, verificationCode) {
    try {
      const result = await doPost('/reset-password', {
        email,
        newPassword,
        code: verificationCode,
      });
      return {
        sucess: true,
        message: 'Senha redefinida com sucesso.',
      };
    } catch (error) {
      const errorMessage = new LoginErrorHandler().handle(error);
      return {
        sucess: false,
        message: errorMessage,
      };
    }
  }

  cleanAndRefresh() {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = window.location.origin;
  }

  async signOut() {
    console.log('logout...');
    try {
      await doGet('/logout', {});
      await amplifySignOut();
      this.cleanAndRefresh();
      localStorage.clear();
      sessionStorage.clear();
      Cookies.remove('token');
    } catch (error) {
      console.error('error signing out: ', error);
    }
  }
})();
